import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import VideoCarousel from '../components/Home/VideoCarousel';
import StatisticsSection from '../components/Home/StatisticsSection';
import KeyFeaturesSection from '../components/Home/KeyFeaturesSection';
import PaperSection from '../components/Home/PaperSection';
import BibTeXSection from '../components/Home/BibTeXSection';
import BlogSection from '../components/Home/BlogSection';
import { fetchPostsHomePage } from '../services/api';
import { Post } from '../types/types';

export default function Home() {
  const [videoSources, setVideoSources] = useState<string[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const hasShownSignupToast = useRef(false);
  const statisticsSectionRef = useRef<HTMLDivElement | null>(null);
  const keyFeaturesSectionRef = useRef<HTMLDivElement | null>(null);
  const bibtexEntry = `@misc{Boxan2026_fomo,
      title={{FoMo: A Multi-Season Dataset for Robot Navigation in For\\^et Montmorency}}, 
      author={Matěj Boxan and Gabriel Jeanson and Alexander Krawciw and Effie Daum and Xinyuan Qiao and Sven Lilge and Timothy D. Barfoot and François Pomerleau},
      year={2026},
      eprint={2603.08433},
      archivePrefix={arXiv},
      primaryClass={cs.RO},
      url={https://arxiv.org/abs/2603.08433}, 
}`;

  const scrollToSection = (
    section: React.RefObject<HTMLDivElement | null>,
    offset = 100
  ) => {
    if (section.current) {
      const top =
        section.current.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setVideoSources(['assets/tease_videos/web-home-page-video_2.mp4']);
  }, []);

  useEffect(() => {
    fetchPostsHomePage()
      .then((processedPosts) => {
        setPosts(
          processedPosts.sort(
            (a: any, b: any) =>
              new Date(b.date).getTime() - new Date(a.date).getTime()
          )
        );
      })
      .catch((error) => console.error('Error loading posts:', error));
  }, []);

  useEffect(() => {
    const fromSignin = sessionStorage.getItem('fromSignin');
    if (fromSignin && !hasShownSignupToast.current) {
      toast.success('Logged in successfully!');
      sessionStorage.removeItem('fromSignin');
      hasShownSignupToast.current = true;
    }
  }, []);

  return (
    <div>
      <section className="relative w-full min-h-screen overflow-hidden">
        <div className="absolute inset-0 z-0">
          <VideoCarousel videoSources={videoSources} />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center text-white text-center px-4 pt-32 pb-12 min-h-screen">
          <h1 className="text-3xl md:text-6xl font-bold max-w-6xl">
            FoMo: A Multi-Season Dataset for Robot Navigation in Forêt
            Montmorency
          </h1>
          <p className="md:text-2xl text-md mb-5 mt-5 max-w-4xl">
            Spanning over 12 months, FoMo is a unique data recording to test the
            limits of lidar, visual and radar-based odometry and SLAM
            algorithms. The recording took place in a boreal forest in southern
            Quebec that sees some truly harsh winter conditions.
          </p>
          <button
            onClick={() => scrollToSection(statisticsSectionRef, 120)}
            className="mt-8 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-black dark:text-white rounded-full flex items-center justify-center gap-2 transition duration-300 dark:hover:bg-blue-600 hover:bg-blue-600"
          >
            <div className="relative flex items-center justify-center">
              <span className="arrow-down"></span>
            </div>
            <span className="text-lg">Statistics and Features</span>
            <div className="relative flex items-center justify-center">
              <span className="arrow-down"></span>
            </div>
          </button>
        </div>
      </section>
      <section className="py-16 bg-white text-black dark:bg-neutral-900 dark:text-white">
        <div ref={statisticsSectionRef} className="mt-1 w-full">
          <StatisticsSection />
        </div>
        <div ref={keyFeaturesSectionRef} className="mt-1 w-full">
          <KeyFeaturesSection />
        </div>
        <BlogSection posts={posts} />
        <div className="my-8"></div>
        <PaperSection paperUrl="https://arxiv.org/pdf/2603.08433" />

        <BibTeXSection bibtexEntry={bibtexEntry} />
      </section>
    </div>
  );
}
